import { useState } from "react";
import { Zap, ArrowLeft } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface Props {
  onBack: () => void;
}

export function ForgotPasswordPage({ onBack }: Props) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--color-bg-base)" }}
    >
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg mb-3"
            style={{ background: "var(--color-brand)" }}
          >
            <Zap size={18} className="text-white" strokeWidth={2.5} />
          </div>
          <h1
            className="text-headline font-extrabold tracking-tighter"
            style={{ color: "var(--color-text-primary)" }}
          >
            Stride
          </h1>
          <p className="text-caption mt-1" style={{ color: "var(--color-text-muted)" }}>
            Reset your password
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-6"
          style={{
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-default)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
          }}
        >
          {success ? (
            <div className="text-center py-2">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: "rgba(16, 185, 129, 0.12)" }}
              >
                <span style={{ color: "var(--color-success)", fontSize: "1.5rem" }}>✓</span>
              </div>
              <h2
                className="text-title font-bold mb-2"
                style={{ color: "var(--color-text-primary)" }}
              >
                Email sent
              </h2>
              <p className="text-caption mb-4" style={{ color: "var(--color-text-muted)" }}>
                Check <strong style={{ color: "var(--color-text-secondary)" }}>{email}</strong> for a password reset link.
              </p>
              <button
                onClick={onBack}
                className="text-caption font-medium transition-colors"
                style={{ color: "var(--color-accent)" }}
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <p className="text-caption" style={{ color: "var(--color-text-muted)" }}>
                Enter your email and we'll send you a link to reset your password.
              </p>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="forgot-email"
                  className="text-caption font-medium"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 rounded-lg text-body transition-colors"
                  style={{
                    background: "var(--color-bg-raised)",
                    border: "1px solid var(--color-border-default)",
                    color: "var(--color-text-primary)",
                  }}
                />
              </div>

              {error && (
                <p
                  className="text-caption px-3 py-2 rounded-lg"
                  style={{
                    color: "#f87171",
                    background: "rgba(248, 113, 113, 0.1)",
                    border: "1px solid rgba(248, 113, 113, 0.2)",
                  }}
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg text-caption font-semibold transition-opacity disabled:opacity-50"
                style={{
                  background: "var(--color-accent)",
                  color: "#fff",
                }}
              >
                {loading ? "Sending..." : "Send reset link"}
              </button>
            </form>
          )}
        </div>

        {/* Back link */}
        {!success && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 mx-auto mt-4 text-caption transition-colors"
            style={{ color: "var(--color-text-muted)" }}
          >
            <ArrowLeft size={13} />
            Back to sign in
          </button>
        )}
      </div>
    </div>
  );
}
